import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {
    ChecklistData,
    ChecklistPartElements,
    CollectionDataResponse,
    CollectionLabel,
    CommentsResponse,
    DibDocument,
    DocgenDocumentResponse,
    DocumentPartsResponse,
    GetChecklistPartElementsArgs,
    InlineRelationsResponse,
    PatchXVariableArgs,
    RawChecklistResponse,
    ResourceConditionsResponse,
    ResourceConditionUserData,
    TopicCollection,
    TopicCollectionsResponse,
} from "../content/types";
import {Status} from "../content/types";
import {checklistFrom} from "../content/checklistTransform";

const componentToTypeMap: Record<string, string> = {
    X_VAR: "dib-x-var",
    X_LIST_ITEM: "dib-x-var",
    X_LIST_COUNTER: "dib-x-list",
    X_ALTERNATIVES: "dib-x-alternatives",
    X_OPTIONAL: "dib-x-optional",
    X_SECTION_OPTIONAL: "dib-x-section",
};

export const dibApi = createApi({
    reducerPath: "dibApi",
    baseQuery: fetchBaseQuery({baseUrl: "/dibapi/api"}),
    tagTypes: ["CollectionData", "Comments", "DocgenDocument"],
    endpoints: (builder) => ({
        getResourceTypeId: builder.query<
            string | undefined,
            {topicId: string | undefined}
        >({
            queryFn: async ({topicId}, _api, _extraOptions, baseQuery) => {
                if (!topicId) return {data: undefined};
                const result = await baseQuery(
                    `resourcetypeid/${encodeURIComponent(topicId)}`
                );
                return result.data !== undefined
                    ? {data: result.data as string}
                    : {error: result.error!};
            },
        }),

        getDocument: builder.query<
            DibDocument,
            {resourceId: string; segmentId?: string}
        >({
            query: ({resourceId, segmentId}) => {
                const queryParams = new URLSearchParams();
                queryParams.set("resourceId", resourceId);
                if (segmentId) {
                    queryParams.set("segmentId", segmentId);
                }
                return `document?${queryParams.toString()}`;
            },
            // TODO! Feilhåndtering
        }),
        getInlineRelations: builder.query<
            InlineRelationsResponse,
            {
                resourceId: string;
                id: string;
                segmentId?: string;
            }
        >({
            query: ({resourceId, id, segmentId}) => {
                const queryParams = new URLSearchParams();
                queryParams.set("resourceId", resourceId);
                queryParams.set("id", id);
                if (segmentId) {
                    queryParams.set("segmentId", id);
                }

                return `document/related?${queryParams.toString()}`;
            },
            // TODO! Feilhåndtering
        }),
        getChecklist: builder.query<
            ChecklistData,
            {topicId: string; isPreview?: boolean}
        >({
            query: ({topicId, isPreview = false}) => {
                const queryParams = new URLSearchParams();
                queryParams.set("resourceId", topicId.toLowerCase());
                if (isPreview) {
                    queryParams.set("cache", "false");
                }
                return `checklist/client?${queryParams.toString()}`;
            },
            transformResponse: (
                response: RawChecklistResponse
            ): ChecklistData => checklistFrom(response),
        }),
        getDocumentParts: builder.query<
            DocumentPartsResponse,
            {
                topicId: string;
                segmentId: string;
                id: string;
            }
        >({
            query: ({topicId, segmentId, id}) => {
                const queryParams = new URLSearchParams();
                queryParams.set("resourceId", topicId);
                queryParams.set("segmentId", segmentId);
                queryParams.set("id", id);
                return `/document?${queryParams.toString()}`;
            },
            // TODO! Feilhåndtering
        }),

        // --- Checklist: lazy element loading ---

        getChecklistPartElements: builder.query<
            ChecklistPartElements,
            GetChecklistPartElementsArgs
        >({
            query: ({resourceId, id, recursive = false, isPreview = false}) => {
                const queryParams = new URLSearchParams();
                queryParams.set("resourceId", resourceId);
                queryParams.set("id", id);
                if (recursive) {
                    queryParams.set("recursive", "true");
                }
                if (isPreview) {
                    queryParams.set("cache", "false");
                }
                return `checklist/partelements?${queryParams.toString()}`;
            },
        }),

        // --- Checklist: collection data ---

        getCollectionData: builder.query<
            CollectionDataResponse,
            {topicId: string; resourceId: string; collectionId: string}
        >({
            query: ({topicId, resourceId, collectionId}) => {
                const queryParams = new URLSearchParams();
                if (collectionId)
                    queryParams.set("collectionId", collectionId.toLowerCase());
                if (topicId) queryParams.set("topicId", topicId.toLowerCase());
                if (resourceId)
                    queryParams.set("resourceId", resourceId.toLowerCase());
                return `checklist/collection?${queryParams.toString()}`;
            },
            providesTags: ["CollectionData"],
        }),

        getComments: builder.query<
            CommentsResponse,
            {topicId: string; collectionId: string}
        >({
            query: ({topicId, collectionId}) =>
                `collectiondata/ci_comment?topicId=${topicId}&collectionId=${collectionId}`,
            transformResponse: (response: CommentsResponse) =>
                response ?? {elementdata: {}, objects: {}},
            providesTags: ["Comments"],
        }),

        // --- Checklist: status mutations ---

        patchStatusForCheckItems: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                itemIds: string[];
                status: Status;
            }
        >({
            query: ({topicId, collectionId, itemIds, status}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    collectionId,
                    id: itemIds,
                    ob: "status",
                    op: "update",
                    topicId,
                    values: {status},
                },
            }),
            async onQueryStarted(
                {topicId, resourceId, collectionId, itemIds, status},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibApi.util.updateQueryData(
                        "getCollectionData",
                        {topicId, resourceId, collectionId},
                        (draft) => {
                            for (const id of itemIds) {
                                draft.checkitems[id] = {
                                    ...(draft.checkitems[id] ?? {
                                        collectionId,
                                        id,
                                    }),
                                    status,
                                };
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        patchDimensions: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                dimensions: string[];
                labels: CollectionLabel[];
            }
        >({
            query: ({topicId, collectionId, dimensions}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    ob: "subset",
                    op: "update",
                    topicId,
                    collectionId,
                    id: dimensions,
                },
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    dibApi.util.updateQueryData(
                        "getCollectionData",
                        {
                            topicId: args.topicId,
                            resourceId: args.resourceId,
                            collectionId: args.collectionId,
                        },
                        (draft) => {
                            draft.labels = args.labels as CollectionLabel[];
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        patchCollectionName: builder.mutation<
            {objects: Record<string, unknown>},
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                name: string;
            }
        >({
            query: ({topicId, collectionId, name}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    ob: "collection",
                    op: "update",
                    segmentId: "",
                    topicId,
                    collectionId,
                    values: {name},
                },
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    dibApi.util.updateQueryData(
                        "getCollectionData",
                        {
                            topicId: args.topicId,
                            resourceId: args.resourceId,
                            collectionId: args.collectionId,
                        },
                        (draft) => {
                            draft.metadata.name = args.name;
                        }
                    )
                );
                try {
                    const {data} = await queryFulfilled;
                    const collectionObj = data.objects[args.collectionId] as
                        | {data: {name: string}}
                        | undefined;
                    if (collectionObj?.data?.name) {
                        dispatch(
                            dibApi.util.updateQueryData(
                                "getCollectionData",
                                {
                                    topicId: args.topicId,
                                    resourceId: args.resourceId,
                                    collectionId: args.collectionId,
                                },
                                (draft) => {
                                    draft.metadata.name =
                                        collectionObj.data.name;
                                }
                            )
                        );
                    }
                } catch {
                    patchResult.undo();
                }
            },
        }),

        patchDocumentSelected: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                documentId: string;
                selected: boolean;
            }
        >({
            query: ({topicId, collectionId, documentId, selected}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    collectionId,
                    ob: "collection",
                    op: "update",
                    values: {
                        topicId: documentId,
                        selected: selected ? "1" : "0",
                    },
                },
            }),
            async onQueryStarted(
                {topicId, resourceId, collectionId, documentId, selected},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibApi.util.updateQueryData(
                        "getCollectionData",
                        {topicId, resourceId, collectionId},
                        (draft) => {
                            if (!selected) {
                                draft.documents = draft.documents.filter(
                                    (doc) => doc.topicId !== documentId
                                );
                            } else {
                                draft.documents.push({
                                    id: documentId,
                                    topicId: documentId,
                                    selected: true,
                                });
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        // --- Checklist: comment mutations ---

        addComment: builder.mutation<
            CommentsResponse,
            {
                topicId: string;
                collectionId: string;
                id: string;
                comment: string;
            }
        >({
            query: ({collectionId, topicId, id, comment}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                },
                body: {
                    collectionId,
                    topicId,
                    id: [id],
                    ob: "ci_comment",
                    op: "add",
                    values: {comment},
                },
            }),
            invalidatesTags: ["Comments"],
        }),

        updateComment: builder.mutation<
            CommentsResponse,
            {
                topicId: string;
                collectionId: string;
                id: string;
                comment: string;
                commentId: string;
            }
        >({
            query: ({collectionId, topicId, id, comment, commentId}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                },
                body: {
                    collectionId,
                    topicId,
                    id: [id],
                    ob: "ci_comment",
                    op: "update",
                    values: {comment, comment_id: commentId},
                },
            }),
            invalidatesTags: ["Comments"],
        }),

        deleteComment: builder.mutation<
            CommentsResponse,
            {
                topicId: string;
                collectionId: string;
                commentId: string;
            }
        >({
            query: ({collectionId, topicId, commentId}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                },
                body: {
                    collectionId,
                    topicId,
                    id: [],
                    ob: "ci_comment",
                    op: "delete",
                    values: {comment_id: commentId},
                },
            }),
            invalidatesTags: ["Comments"],
        }),

        // --- Checklist: collection lifecycle ---

        createCollection: builder.mutation<
            {collectionId: string; name: string},
            {
                topicId: string;
                collectionId: string;
                name: string;
                dimensions: string[];
            }
        >({
            query: ({topicId, collectionId, name, dimensions}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    collectionId,
                    segmentId: "",
                    ob: "collection",
                    op: "saveas",
                    id: dimensions,
                    values: {
                        name,
                        ok: "0",
                        na: "0",
                        comment: "0",
                        variables: "0",
                    },
                },
            }),
        }),

        deleteFilling: builder.mutation<
            void,
            {topicId: string; collectionId: string}
        >({
            query: ({topicId, collectionId}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    collectionId,
                    ob: "collection",
                    op: "delete",
                    segmentId: "",
                    id: [collectionId],
                },
            }),
            invalidatesTags: ["CollectionData"],
        }),

        // --- Checklist: topic collections ---

        getTopicCollections: builder.query<
            TopicCollection[],
            {topicId: string}
        >({
            query: ({topicId}) =>
                `collectiondata/collection?topicId=${topicId}`,
            transformResponse: (response: TopicCollectionsResponse) => {
                try {
                    return response.root
                        .map((id) => response.objects[id])
                        .filter((c) => c.data.draft === 0);
                } catch {
                    return [];
                }
            },
            providesTags: ["CollectionData"],
        }),

        duplicateCollection: builder.mutation<
            {collectionId: string; name: string},
            {
                collectionId: string;
                topicId: string;
                name: string;
                comment: boolean;
                na: boolean;
                ok: boolean;
                deviation: boolean;
                variables: boolean;
            }
        >({
            query: ({
                topicId,
                collectionId,
                name,
                ok,
                na,
                comment,
                deviation,
                variables,
            }) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    collectionId,
                    topicId,
                    ob: "collection",
                    op: "saveas",
                    values: {name, ok, na, comment, deviation, variables},
                },
            }),
        }),

        // ─── Document Generator Endpoints ────────────────────────────
        getDocgenDocument: builder.query<
            DocgenDocumentResponse,
            {resourceId: string; collectionId?: string}
        >({
            query: ({resourceId, collectionId}) => {
                const params = new URLSearchParams();
                params.set("resourceId", resourceId);
                if (collectionId) params.set("collectionId", collectionId);
                return `document?${params.toString()}`;
            },
            transformResponse: (response: any): DocgenDocumentResponse => {
                const topicData = response;

                // Port of dibDocumentFrom's autoCount element
                // restructuring from DibApp2020.
                // Elements with autoCount wrap their non-header
                // children in a synthetic div.dib-x-section__children
                const HEADERS = ["h1", "h2", "h3", "h4", "h5", "h6"];
                const isHeader = (el: any) =>
                    HEADERS.includes((el?.name || "").toLowerCase());

                const newElements = Object.keys(
                    topicData.elements || {}
                ).reduce(
                    (acc: any, curr: string) => {
                        const element = topicData.elements[curr];
                        const obj = topicData.objects?.[curr];
                        const isAutoCount =
                            obj?.data?.autoCount === true;

                        if (
                            isAutoCount &&
                            element.children?.length > 0
                        ) {
                            const firstChild = element.children[0];

                            // No valid header → disable autoCount
                            if (
                                "text" in firstChild ||
                                ("id" in firstChild &&
                                    !isHeader(
                                        topicData.elements[
                                            firstChild.id
                                        ]
                                    ))
                            ) {
                                obj.data.autoCount = false;
                                return acc;
                            }

                            // Hidden title
                            if (
                                "id" in firstChild &&
                                topicData.elements[
                                    firstChild.id
                                ]?.attributes?.class?.includes(
                                    "dib-x-hide"
                                )
                            ) {
                                const childWrapperId = `${curr}-children`;
                                acc[childWrapperId] = {
                                    name: "div",
                                    attributes: {
                                        id: `${element.attributes?.id}-children`,
                                        class: "dib-x-section__children",
                                    },
                                    children: [
                                        ...element.children.slice(1),
                                    ],
                                };
                                acc[curr] = {
                                    ...element,
                                    children: [{id: childWrapperId}],
                                };
                                return acc;
                            }

                            // Normal header + content
                            const childWrapperId = `${curr}-children`;
                            acc[childWrapperId] = {
                                name: "div",
                                attributes: {
                                    id: `${element.attributes?.id}-children`,
                                    class: "dib-x-section__children",
                                },
                                children: [
                                    ...element.children.slice(1),
                                ],
                            };
                            acc[curr] = {
                                ...element,
                                children: [
                                    firstChild,
                                    {id: childWrapperId},
                                ],
                            };
                        }
                        return acc;
                    },
                    {...topicData.elements}
                );

                topicData.elements = newElements;

                return {
                    topicData,
                    metadata: {
                        topicId: topicData.topicId,
                        id: topicData.id,
                        name: topicData.name,
                        resourceTypeId: topicData.resourceTypeId,
                    },
                };
            },
            providesTags: ["DocgenDocument"],
        }),
        getResourceConditions: builder.query<
            ResourceConditionsResponse,
            {resourceId: string}
        >({
            query: ({resourceId}) =>
                `conditions/${encodeURIComponent(resourceId)}`,
        }),
        getResourceConditionUserData: builder.query<
            ResourceConditionUserData,
            {topicId: string; collectionId?: string}
        >({
            query: ({topicId, collectionId}) =>
                `condition-label-data/${encodeURIComponent(topicId)}/${encodeURIComponent(collectionId ?? "")}`,
        }),
        patchXVariable: builder.mutation<void, PatchXVariableArgs>({
            query: ({topicId, collectionId, component, id, value, n}) => ({
                url: "collectiondata",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    ob: "uservalues",
                    op: "update",
                    collectionId,
                    topicId,
                    values: {
                        type: componentToTypeMap[component] ?? component,
                        id,
                        n: component === "X_LIST_ITEM" ? n : "0",
                        value,
                    },
                },
            }),
        }),
        setResourceCondition: builder.mutation<
            ResourceConditionUserData,
            {
                documentIds: {topicId: string; collectionId?: string};
                conditionLabelGroup: string;
                conditionLabel: string;
            }
        >({
            query: ({documentIds, conditionLabelGroup, conditionLabel}) => ({
                url: "condition-label-data",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId: documentIds.topicId,
                    collectionId: documentIds.collectionId ?? null,
                    labelGroup: conditionLabelGroup,
                    label: conditionLabel,
                },
            }),
            async onQueryStarted(
                {documentIds, conditionLabelGroup, conditionLabel},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibApi.util.updateQueryData(
                        "getResourceConditionUserData",
                        {
                            topicId: documentIds.topicId,
                            collectionId: documentIds.collectionId,
                        },
                        (draft) => {
                            const idx = draft.findIndex(
                                (item) =>
                                    item.label_group === conditionLabelGroup
                            );
                            const entry = {
                                topic_id: documentIds.topicId,
                                collection_id:
                                    documentIds.collectionId ?? "",
                                label_group: conditionLabelGroup,
                                label: conditionLabel,
                            };
                            if (idx >= 0) {
                                draft[idx] = entry;
                            } else {
                                draft.push(entry);
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        // ─── Document Generator: company autofill ───────────────────
        autofillDocumentForOrgNr: builder.query<
            DocgenDocumentResponse,
            {
                orgnr: string;
                variableId: string;
                triggerName: string;
                documentIds: {topicId: string; collectionId?: string};
            }
        >({
            query: ({orgnr, documentIds, triggerName}) => {
                const params = new URLSearchParams();
                params.set("orgId", orgnr);
                params.set("triggerId", triggerName);
                if (documentIds.collectionId)
                    params.set("collectionId", documentIds.collectionId);
                if (documentIds.topicId)
                    params.set("topicId", documentIds.topicId);
                return `document/companydata/?${params}`;
            },
            async onQueryStarted(_args, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(dibApi.util.invalidateTags(["DocgenDocument"]));
                } catch (err) {
                    console.error("Could not autofill document:", err);
                }
            },
        }),

        // ─── Document Generator: reset ──────────────────────────────
        resetXVariables: builder.mutation<
            void,
            {topicId: string; collectionId: string}
        >({
            query: ({topicId, collectionId}) => ({
                url: "collectiondata",
                method: "DELETE",
                headers: {"Content-Type": "application/json"},
                body: {
                    ob: "uservalues",
                    op: "clear",
                    collectionId,
                    topicId,
                },
            }),
            invalidatesTags: ["DocgenDocument"],
        }),
        resetConditionalUserData: builder.mutation<
            void,
            {topicId: string; collectionId?: string}
        >({
            query: ({topicId, collectionId}) => ({
                url: "condition-label-data",
                method: "DELETE",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    collectionId: collectionId || null,
                },
            }),
        }),
    }),
});
